import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  SideNavGroup,
  SideNavLink,
  SideNavLinkItemType,
} from '@amzn/aac-chrome-internal-api';
import { angleDown, angleInlineEnd } from '@amzn/storm-ui-icons';
import {
  AccordionContentGroupWrapper,
  AccordionContentInnerWrapper,
  AccordionContentOuterWrapper,
  AccordionContentSubTitle,
  AccordionLink,
  AccordionLinkWrapper,
  StyledDivider as Divider,
  SubGroupCollapseInner,
  SubGroupCollapseWrapper,
  SubGroupToggleButton,
} from '../styles';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import SideNavLinkFeatures from 'src/features/nav/components/ConsolidatedSideNav/SideNavLinkFeatures/SideNavLinkFeatures';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { CAMPAIGNS_MENU_ITEM_ID } from 'src/commons/constants';
import { useLinkUpdater } from 'src/commons/utils/linkUpdater';
import { useInterstitialModalIntercept } from '../InterstitialModal/useInterstitialModalIntercept';

interface AccordionContentProps {
  id: string;
  cbSelected?: boolean;
  allToolsEnabled: boolean;
}

interface AccordionLinkItemProps {
  link: SideNavLink;
  isSelected: boolean;
  isNavOpen: boolean;
  isFocusable: boolean;
  resolvedUrl: string;
  allToolsEnabled: boolean;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const AccordionLinkItem: React.VFC<AccordionLinkItemProps> = ({
  link,
  isSelected,
  isNavOpen,
  isFocusable,
  resolvedUrl,
  allToolsEnabled,
  onClick,
}) => {
  const isExternalLink = link.sidebarItemType === 'EXTERNAL_LINK';
  const isModal = link.sidebarItemType === ('MODAL' as SideNavLinkItemType);
  return (
    <AccordionLinkWrapper
      isSelected={isSelected}
      isNavOpen={isNavOpen}
      data-ccx-e2e-id={`${link.id}-accordion-link-wrapper`}
    >
      <AccordionLink
        isSelected={isSelected}
        href={isModal ? 'javascript:void(0)' : resolvedUrl}
        target={isExternalLink ? '_blank' : '_self'}
        data-ccx-e2e-id={link.id}
        tabIndex={isNavOpen && isFocusable ? 0 : -1}
        aria-label={link.link.label}
        onClick={onClick}
        {...(link.isSelected && { 'aria-current': 'page' })}
        {...getTaktAttributes(
          link.id,
          chromeComponents.NAV,
          {
            url: resolvedUrl,
          },
          allToolsEnabled,
        )}
      >
        {link.link.label}
      </AccordionLink>
      <SideNavLinkFeatures sideNavLink={link} isFocusable={isFocusable} />
    </AccordionLinkWrapper>
  );
};

const AccordionContent: React.VFC<AccordionContentProps> = ({
  id,
  cbSelected = false,
  allToolsEnabled,
}) => {
  const chromeConfig = useChromeConfigContext();
  const transformUrl = useLinkUpdater();
  const {
    isNavOpen,
    toolbarItems,
    setSelectedNavItemId,
    setOpenModalAppName,
    activeCategoryId,
    updateSelectedNavItem,
  } = useConsolidatedNavContext();
  const tryInterceptInterstitial = useInterstitialModalIntercept();
  const toolbarItem = useMemo(
    () => toolbarItems.find((toolbarItem) => toolbarItem.id === id),
    [toolbarItems],
  );

  const [expandedSubGroupKey, setExpandedSubGroupKey] = useState<string | null>(
    null,
  );
  const seededRef = useRef(false);
  const lastSelectedGroupKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!toolbarItem?.sidebarGroups?.length) {
      return;
    }
    const groupWithSelectedLink = toolbarItem.sidebarGroups.findIndex(
      (group) =>
        group.collapsible &&
        group.links.length > 1 &&
        group.links.some((link) => link.isSelected),
    );
    if (groupWithSelectedLink !== -1) {
      const selectedKey = `${toolbarItem.id}::${groupWithSelectedLink}`;
      if (lastSelectedGroupKeyRef.current !== selectedKey) {
        lastSelectedGroupKeyRef.current = selectedKey;
        setExpandedSubGroupKey(selectedKey);
      }
      seededRef.current = true;
      return;
    }
    lastSelectedGroupKeyRef.current = null;
    if (seededRef.current) {
      return;
    }
    seededRef.current = true;
    const firstDefaultExpanded = toolbarItem.sidebarGroups.findIndex(
      (group) =>
        group.collapsible && group.links.length > 1 && group.defaultExpanded,
    );
    if (firstDefaultExpanded !== -1) {
      setExpandedSubGroupKey(`${toolbarItem.id}::${firstDefaultExpanded}`);
    }
  }, [toolbarItem?.id, toolbarItem?.sidebarGroups]);

  const toggleSubGroup = useCallback((key: string) => {
    setExpandedSubGroupKey((prev) => (prev === key ? null : key));
  }, []);

  if (!toolbarItem) {
    return null;
  }

  const { sidebarGroups: groups } = toolbarItem;
  const isActive: boolean = activeCategoryId === id;

  const renderLink = (link: SideNavLink, linkIsFocusable: boolean) => {
    const linkIsSelected =
      link.isSelected || (cbSelected && link.id === CAMPAIGNS_MENU_ITEM_ID);
    const resolvedUrl =
      link.id === CAMPAIGNS_MENU_ITEM_ID && transformUrl
        ? transformUrl(link.link.url)
        : link.link.url;
    return (
      <AccordionLinkItem
        key={link.id}
        link={link}
        isSelected={linkIsSelected}
        isNavOpen={isNavOpen}
        isFocusable={linkIsFocusable}
        resolvedUrl={resolvedUrl}
        allToolsEnabled={allToolsEnabled}
        onClick={(event) => {
          NavMetricPublisher.publishCounterMonitor(
            `sidenavItemClicked-${link.id}`,
            1,
          );

          if (link.sidebarItemType === ('MODAL' as SideNavLinkItemType)) {
            setOpenModalAppName(link.appName ?? '');
            return;
          }

          if (tryInterceptInterstitial(link.link.url, link.appName, event)) {
            return;
          }

          // Do not update the currently selected app when Client Side Routing is not enabled
          if (!link.isCSREnabled) {
            return;
          }

          // Do not update the currently selected app when a link is opened in a new tab or a new window
          if (event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
          }

          // Updating selected nav item so that the change would get reflected in header
          updateSelectedNavItem?.(toolbarItem.id, link.id);

          // check if csr is used
          const { onRouteClick } = chromeConfig;
          if (onRouteClick) {
            event.preventDefault();
            onRouteClick({
              url: link.link.url,
              id: link.id,
            });
            setSelectedNavItemId(link.id);
            NavMetricPublisher.publishCounterMonitor(
              `sidenavItemClientSideRoutingClicked-${link.id}`,
              1,
            );
          }
        }}
      />
    );
  };

  return (
    <AccordionContentOuterWrapper isActive={isActive} isNavOpen={isNavOpen}>
      <AccordionContentInnerWrapper isActive={isActive}>
        {groups.map((group: SideNavGroup, index: number) => {
          const shouldFlattenSidebarGroupWithSingleLink =
            !!group.collapsible && group.links.length === 1;
          const isCollapsibleGroup =
            !!(group.collapsible && group.title) &&
            !shouldFlattenSidebarGroupWithSingleLink;
          const subGroupKey = `${toolbarItem.id}::${index}`;
          const toggleId = `${subGroupKey}-toggle`;
          const panelId = `${subGroupKey}-panel`;
          const isExpanded = expandedSubGroupKey === subGroupKey;

          return (
            <AccordionContentGroupWrapper
              key={subGroupKey}
              direction="column"
              rowGap="xxxsmall"
            >
              {isCollapsibleGroup ? (
                <>
                  <SubGroupToggleButton
                    id={toggleId}
                    type="button"
                    variant="transparent"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    tabIndex={isNavOpen && isActive ? 0 : -1}
                    data-ccx-e2e-id={toggleId}
                    iconEnd={isExpanded ? angleDown : angleInlineEnd}
                    onClick={() => {
                      toggleSubGroup(subGroupKey);
                      NavMetricPublisher.publishCounterMonitor(
                        `sidenavSubGroupToggled-${toolbarItem.id}-${index}`,
                        1,
                      );
                    }}
                  >
                    {group.title}
                  </SubGroupToggleButton>
                  <SubGroupCollapseWrapper
                    id={panelId}
                    isExpanded={isExpanded}
                    role="region"
                    aria-labelledby={toggleId}
                    {...(!isExpanded && { inert: '' })}
                  >
                    <SubGroupCollapseInner>
                      {group.links.map((link) =>
                        renderLink(link, isActive && isExpanded),
                      )}
                    </SubGroupCollapseInner>
                  </SubGroupCollapseWrapper>
                </>
              ) : (
                <>
                  {group.title && !shouldFlattenSidebarGroupWithSingleLink && (
                    <AccordionContentSubTitle>
                      {group.title}
                    </AccordionContentSubTitle>
                  )}
                  {group.links.map((link) => renderLink(link, isActive))}
                </>
              )}
              {index !== groups.length - 1 && <Divider />}
            </AccordionContentGroupWrapper>
          );
        })}
      </AccordionContentInnerWrapper>
    </AccordionContentOuterWrapper>
  );
};

export default AccordionContent;
